import * as zod from 'zod';

export function stringToNumber(input: string | number | null): number | null {
  if (typeof input === 'number' && !isNaN(input)) {
    return input;
  } else if (input) {
    return Number(input);
  } else {
    return null;
  }
}

export function valueOrNull(value: string | number): string | number | null {
  if (typeof value === 'string' && value === '') {
    return null;
  }
  return value;
}

/**
 * Cleans numeric input by removing commas and trimming whitespace
 */
export function cleanNumericInput(val: string): string {
  return val.replace(/,/g, '').trim();
}

/**
 * Parses a decimal string, handling commas and returning null for invalid input
 */
export function decimalParser(val: string): string | null {
  if (!val || val.trim() === '') return null;
  const cleanVal = cleanNumericInput(val);
  const num = parseFloat(cleanVal);
  return isNaN(num) ? null : cleanVal;
}

/**
 * Transforms empty strings to null, trims non-empty strings
 */
export const optionalStringToNull = zod
  .string()
  .optional()
  .transform((val) => (val === '' || !val ? null : val.trim()));

/**
 * Requires a non-empty string after trimming
 */
export function requiredString(errorMessage = 'This field is required') {
  return zod
    .string()
    .min(1, errorMessage)
    .transform((val) => val.trim());
}

/**
 * Transforms string to number, handling empty strings as null
 * Removes commas before parsing
 */
export const optionalStringToNumber = zod
  .string()
  .optional()
  .transform((val) => {
    if (!val || val.trim() === '') return null;
    const cleanVal = cleanNumericInput(val);
    const num = parseFloat(cleanVal);
    return isNaN(num) ? null : num;
  });

/**
 * Validates that a string represents a positive number (> 0)
 */
export function positiveNumberString(
  errorMessage = 'Must be a positive number',
) {
  return zod.string().refine((val) => {
    if (!val || val.trim() === '') return false;
    const num = parseFloat(cleanNumericInput(val));
    return !isNaN(num) && num > 0;
  }, errorMessage);
}

/**
 * Validates that a string represents a non-negative number (>= 0)
 */
export function nonNegativeNumberString(
  errorMessage = 'Must be a non-negative number',
) {
  return zod.string().refine((val) => {
    if (!val || val.trim() === '') return false;
    const num = parseFloat(cleanNumericInput(val));
    return !isNaN(num) && num >= 0;
  }, errorMessage);
}

/**
 * Optional field that must be a positive number if provided
 */
export function optionalPositiveNumberString(
  errorMessage = 'Must be a positive number',
) {
  return zod
    .string()
    .optional()
    .transform((val) => (val === '' ? null : val))
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      const num = parseFloat(cleanNumericInput(val));
      return !isNaN(num) && num > 0;
    }, errorMessage);
}

/**
 * Optional field that must be non-negative if provided
 */
export function optionalNonNegativeNumberString(
  errorMessage = 'Must be a non-negative number',
) {
  return zod
    .string()
    .optional()
    .transform((val) => (val === '' ? null : val))
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      const num = parseFloat(cleanNumericInput(val));
      return !isNaN(num) && num >= 0;
    }, errorMessage);
}

/**
 * Required field that must be a positive number
 */
export function requiredPositiveNumberString(
  requiredMessage = 'This field is required',
  positiveMessage = 'Must be a positive number',
) {
  return zod
    .string()
    .min(1, requiredMessage)
    .refine((val) => {
      const num = parseFloat(cleanNumericInput(val));
      return !isNaN(num) && num > 0;
    }, positiveMessage);
}

/**
 * Required field that must be non-negative
 */
export function requiredNonNegativeNumberString(
  requiredMessage = 'This field is required',
  nonNegativeMessage = 'Must be a non-negative number',
) {
  return zod
    .string()
    .min(1, requiredMessage)
    .refine((val) => {
      const num = parseFloat(cleanNumericInput(val));
      return !isNaN(num) && num >= 0;
    }, nonNegativeMessage);
}

/**
 * Transforms string to number with decimal parsing, handling commas
 */
export function requiredStringToNumber(
  requiredMessage = 'This field is required',
  errorMessage = 'Must be a valid number',
) {
  return zod
    .string()
    .min(1, requiredMessage)
    .transform((val) => {
      const parsed = decimalParser(val);
      return parsed ? Number(parsed) : null;
    })
    .refine((val) => val !== null, errorMessage);
}

/**
 * Optional string to decimal number transformation
 */
export const optionalStringToDecimalNumber = zod
  .string()
  .optional()
  .transform((val) => {
    if (!val) return null;
    const parsed = decimalParser(val);
    return parsed ? Number(parsed) : null;
  });

/**
 * Creates a schema for optional numeric fields with comma support
 * Transforms to null for empty values, preserves original string for non-empty values
 */
export function optionalNumericField() {
  return zod
    .string()
    .optional()
    .transform((val) => {
      if (!val || val.trim() === '') return null;
      const num = parseFloat(val.replace(/,/g, ''));
      return isNaN(num) ? null : val;
    });
}

export const dateToString = zod.date().transform((date) => date.toISOString());

export const nullableDateToString = zod
  .date()
  .nullable()
  .transform((date) => date?.toISOString() ?? null);

export function isInEnum<T extends object>(
  e: T,
  str: unknown,
): str is T[keyof T] {
  return Object.values(e).includes(str as T[keyof T]);
}

type EnumType = { [key: string]: string };

export function nullableStrToEnum<T extends EnumType>(e: T) {
  return zod
    .nativeEnum(e)
    .nullable()
    .transform((str) => (isInEnum(e, str) ? str : null));
}

export const reactSelectOptionSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .transform((obj) => (Object.keys(obj).length === 0 ? null : obj));
